import { CheckoutStatus, ReservationStatus } from '../../../@types/graphql.d';
import {
	CheckoutConflictItem,
	getCheckoutItemConflict,
	getReservationItemConflict,
	ItemConflict,
	ReservationConflictItem,
} from './get-item-conflict';

export enum KitConflict {
	AVAILABLE = 'AVAILABLE',
	UNAVAILABLE = 'UNAVAILABLE',
	RESTRICTED = 'RESTRICTED',
	PARTIALLY_AVAILABLE = 'PARTIALLY_AVAILABLE',
	PARTIALLY_RESTRICTED = 'PARTIALLY_RESTRICTED',
	AT_WRONG_LOCATION = 'WRONG_LOCATION',
	CHECKED_OUT = 'CHECKED_OUT',
	ASSIGNED = 'IN_CUSTODY',
	CHECKING_OUT = 'CHECKING_OUT',
	FLAGGED = 'FLAGGED',
	RESERVED = 'RESERVED',
	CANNOT_CHECKOUT = 'CANNOT_CHECKOUT',
	CANNOT_RESERVE = 'CANNOT_RESERVE',
	RETIRED = 'EXPIRED',
}

export type CheckoutConflictKit = { items: CheckoutConflictItem[] };
export type ReservationConflictKit = { items: ReservationConflictItem[] };

type ReservationContext = {
	bookingLocationId: string | null;
	bookingStatus: ReservationStatus | null;
	isMaintenanceUser?: boolean;
};

type CheckoutContext = {
	bookingId: string | null;
	bookingLocationId: string | null;
	bookingStatus: CheckoutStatus | null;
	isMaintenanceUser?: boolean;
};

export function getKitConflict(
	kit: ReservationConflictKit | CheckoutConflictKit,
	rule: 'reservation' | 'check-out',
	context: ReservationContext | CheckoutContext
): KitConflict;
export function getKitConflict<Rule extends 'reservation' | 'check-out'>(
	kit: Rule extends 'reservation' ? ReservationConflictKit : CheckoutConflictKit,
	rule: Rule,
	context: Rule extends 'reservation' ? ReservationContext : CheckoutContext
): KitConflict {
	switch (rule) {
		case 'check-out': {
			const currentKit = kit as CheckoutConflictKit;
			const currentContext = context as CheckoutContext;
			return getCheckoutKitConflict(currentKit, {
				bookingLocationId: currentContext.bookingLocationId,
				checkoutStatus: currentContext.bookingStatus,
				checkoutId: currentContext.bookingId,
				isMaintenanceUser: currentContext.isMaintenanceUser,
			});
		}
		case 'reservation': {
			const currentKit = kit as ReservationConflictKit;
			const currentContext = context as ReservationContext;

			return getReservationKitConflict(currentKit, {
				bookingLocationId: currentContext.bookingLocationId,
				bookingStatus: currentContext.bookingStatus,
				isMaintenanceUser: currentContext.isMaintenanceUser,
			});
		}
		default:
			throw new Error(`Unknown rule: ${rule}`);
	}
}

function getKitConflictFromItemConflicts(itemConflicts: ItemConflict[]): KitConflict {
	const areAllItemsConflicted = itemConflicts.every((conflict) => conflict !== ItemConflict.AVAILABLE);
	if (areAllItemsConflicted) {
		const uniqueItemConflicts = new Set(itemConflicts);

		if (uniqueItemConflicts.size === 1) {
			switch (itemConflicts[0]) {
				case ItemConflict.RESTRICTED:
					return KitConflict.RESTRICTED;
				case ItemConflict.AT_WRONG_LOCATION:
					return KitConflict.AT_WRONG_LOCATION;
				case ItemConflict.CHECKED_OUT:
					return KitConflict.CHECKED_OUT;
				case ItemConflict.CHECKING_OUT:
					return KitConflict.CHECKING_OUT;
				case ItemConflict.ASSIGNED:
					return KitConflict.ASSIGNED;
				case ItemConflict.FLAGGED:
					return KitConflict.FLAGGED;
				case ItemConflict.RESERVED:
					return KitConflict.RESERVED;
				case ItemConflict.CANNOT_CHECKOUT:
					return KitConflict.CANNOT_CHECKOUT;
				case ItemConflict.CANNOT_RESERVE:
					return KitConflict.CANNOT_RESERVE;
				case ItemConflict.RETIRED:
					return KitConflict.RETIRED;
			}
		}

		return KitConflict.UNAVAILABLE;
	}

	const someItemsAreConflicted = itemConflicts.some((conflict) => conflict !== ItemConflict.AVAILABLE);
	if (someItemsAreConflicted) {
		if (itemConflicts.includes(ItemConflict.RESTRICTED)) {
			return KitConflict.PARTIALLY_RESTRICTED;
		}

		return KitConflict.PARTIALLY_AVAILABLE;
	}

	return KitConflict.AVAILABLE;
}

function getCheckoutKitConflict(
	kit: CheckoutConflictKit,
	context: {
		bookingLocationId: string | null;
		checkoutStatus: CheckoutStatus | null;
		checkoutId: string | null;
		isMaintenanceUser?: boolean;
		isExtending?: boolean;
	}
) {
	const itemConflicts = kit.items.map((item) => {
		return getCheckoutItemConflict(item, {
			checkoutLocation: context.bookingLocationId,
			checkoutStatus: context.checkoutStatus,
			checkoutId: context.checkoutId,
			isMaintenanceUser: context.isMaintenanceUser,
			isExtending: context.isExtending,
		});
	});

	return getKitConflictFromItemConflicts(itemConflicts);
}

function getReservationKitConflict(
	kit: ReservationConflictKit,
	context: {
		bookingLocationId: string | null;
		bookingStatus: ReservationStatus | null;
		isMaintenanceUser?: boolean;
	}
) {
	const itemConflicts = kit.items.map((item) => {
		return getReservationItemConflict(item, {
			reservationLocation: context.bookingLocationId,
			reservationStatus: context.bookingStatus,
			isMaintenanceUser: context.isMaintenanceUser,
		});
	});

	return getKitConflictFromItemConflicts(itemConflicts);
}
